import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Field } from 'redux-form'
import { pt, buildPropTypes } from 'common-data/proptypehelpers'
import { FieldLayoutBox } from './FieldLayoutBox'
import { FieldValueFormatter, FieldValueParser } from './FieldValueConverters'
import { ah, ReduxWrapper, compose, QueryWrapper } from 'common-data/datahelpers'
import { Input, InputGroup, InputGroupAddon, Button } from 'reactstrap'
import _ from 'common-tools/LodashHelper'
import { componentGetCompareProps } from 'common-tools/index'
import { Autocomplete, TextField, Grid, Box, Typography } from '@mui/material'
import { FAIcon } from './FAIcon'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import debounce from 'lodash/debounce'

class AutoSuggestBoxClassPlain2 extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      suggestionList: [],
      selectedSuggestion: {}
    }
  }

  static propTypes = buildPropTypes([], {
    queryName: pt.string.isRequired,
    restoreValue: pt.any,
    hideClear: pt.bool.isRequired,
    extraDataMap: pt.oneOfType([
      pt.string,
      pt.arrayOf(
        pt.shape({
          Source: pt.string,
          Dest: pt.string
        })
      ),
      pt.func
    ]),
    overrideSelectedSuggestion: pt.func,
    readonly: pt.bool
  });

  static defaultProps = {
    hideClear: false,
    readonly: false
  };

  onSuggestionsClearRequested () {
    this.setState({
      suggestionList: []
    })
  }

  componentDidMount () {
    const { restoreValue } = this.props

    if (restoreValue) {
      this.restoreSuggestion(restoreValue)
    }
  }

  componentDidUpdate (prevProps) {
    const restoreValue = componentGetCompareProps('restoreValue', this.props, prevProps, undefined)

    if (restoreValue.HasChanged) {
      console.log('Restoring suggestion for:', restoreValue.Current)
      this.restoreSuggestion(restoreValue.Current)
    }

    // Check if the data has finished loading and update state
    if (prevProps.data.loadingGetSuggestions && !this.props.data.loadingGetSuggestions) {
      console.log(this.props.data)
      if (!this.state.suggestionList.length) {
        console.log(this.props.data)
        this.setState({
          suggestionList: this.props.data.suggestions || []
        })
      }
    }
  }

  fetchSuggestions = _.debounce((value) => {
    const { queryName, GetSuggestions, formCheckForSubmissionErrors, overrideSuggestionList } = this.props

    GetSuggestions({
      Name: queryName,
      Type: queryName,
      Text: value,
      RestoreValue: value
    })
      .then(({ data }) => {
        let newData = _.clone(data)
        if (_.isFunction(overrideSuggestionList)) {
          overrideSuggestionList(newData)
        }
        this.setState({
          suggestionList: newData || []
        })
      })
      .catch(formCheckForSubmissionErrors)
  }, 300);

  onSuggestionsFetchRequested (event, value, reason) {
    if (reason === 'input') {
      this.fetchSuggestions(value)
    }
  }

  onSuggestionSelected (event, { suggestion }) {
    if (suggestion.Value === 'No Matches' || suggestion.Value === 'Searching...') {
      event.stopPropagation()
      event.nativeEvent.stopImmediatePropagation()
      return
    }

    const {
      input: { onChange },
      formSetFieldValue,
      meta: { form },
      extraDataMap,
      id,
      overrideSelectedSuggestion
    } = this.props

    this.setState(
      {
        selectedSuggestion: suggestion
      },
      () => {
        onChange(suggestion.Value)

        if (_.isFunction(overrideSelectedSuggestion)) {
          overrideSelectedSuggestion(suggestion, this.clearSelectedSuggestion)
        }

        if (extraDataMap) {
          let extraData = suggestion.ExtraData || {}
          if (_.isString(extraDataMap)) {
            formSetFieldValue(form, extraDataMap, suggestion.Text)
          } else if (_.isArray(extraDataMap)) {
            for (var mapInfo of extraDataMap) {
              if (_.has(extraData, mapInfo.Source)) {
                formSetFieldValue(form, mapInfo.Dest, extraData[mapInfo.Source])
              }
            }
          } else if (_.isFunction(extraDataMap)) {
            extraDataMap(suggestion, extraData, (field, value) => {
              formSetFieldValue(form, field, value, 'set')
            })
          } else {
            throw Error('Invalid extraDataMap, must be a string, array, or function!')
          }
        }
      }
    )
  }

  clearSelectedSuggestion () {
    const {
      input: { onChange },
      formSetFieldValue,
      meta: { form },
      extraDataMap
    } = this.props

    this.setState(
      {
        selectedSuggestion: {}
      },
      () => {
        onChange(null)
        if (extraDataMap) {
          if (_.isString(extraDataMap)) {
            formSetFieldValue(form, extraDataMap, null)
          } else if (_.isArray(extraDataMap)) {
            for (var mapInfo of extraDataMap) {
              formSetFieldValue(form, mapInfo.Dest, mapInfo.ClearValue)
            }
          } else if (_.isFunction(extraDataMap)) {
            extraDataMap({}, {}, (field, value) => {
              formSetFieldValue(form, field, value, 'clear')
            })
          } else {
            throw Error('Invalid extraDataMap, must be a string, array, or function!')
          }
        }
      }
    )
  }

  restoreSuggestion (value) {
    const { queryName, GetSuggestions, formCheckForSubmissionErrors } = this.props

    GetSuggestions({
      Name: queryName,
      Type: queryName,
      Text: null,
      Value: value
    })
      .then(({ data }) => {
        console.log(data)
        this.setState({
          selectedSuggestion: data[0] || {}
        })
      })
      .catch(formCheckForSubmissionErrors)
  }

  render () {
    const { data, hideClear, children, readonly: lreadonly, id, ...rest } = this.props
    const { suggestionList, selectedSuggestion } = this.state

    let suggestions = suggestionList.length > 0 ? suggestionList : [{ Value: 'No Matches' }]

    if (data.loadingGetSuggestions) {
      suggestions = [{ Value: 'Searching...' }]
    }

    return (
      <FieldLayoutBox {...rest}>
        {({ showError, value, onChange, name, placeHolder = 'Start typing to search', loading, readonly }) => {
          if (selectedSuggestion.Value) {
            if (data.loadingGetSuggestions) {
              return <Input type="text" readOnly value="Fetching..." />
            } else if (lreadonly || hideClear || readonly) {
              return <Input type="text" readOnly value={selectedSuggestion.Text} />
            } else {
              return (
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Input
                    type='text'
                    readOnly
                    value={selectedSuggestion.Text || value || ''}
                    style={{ paddingRight: '2.25rem' }}
                  />
                  <button
                    type='button'
                    onClick={this.clearSelectedSuggestion.bind(this)}
                    title='Clear selection'
                    aria-label='Clear selection'
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      padding: '4px 6px',
                      cursor: 'pointer',
                      color: '#888',
                      fontSize: '0.9rem',
                      lineHeight: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      zIndex: 5,
                      outline: 'none',
                      transition: 'color 0.15s ease-in-out, background-color 0.15s ease-in-out'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#FF0000'
                      e.currentTarget.style.backgroundColor = 'rgba(255, 0, 0, 0.1)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#888'
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <FAIcon name='times' />
                  </button>
                </div>
              )
            }
          } else {
            return (
              <Autocomplete
                id={`textsuggest${id}`}
                options={suggestions}
                isOptionEqualToValue={(option, value) => option.Value === value.Value}
                getOptionLabel={(option) => option.Text || option.Value || ''}
                renderInput={(params) => <TextField {...params} label="Person" placeholder={placeHolder} />}
                value={selectedSuggestion}
                onInputChange={(_, value) => this.onSuggestionsFetchRequested(_, value, 'input')}
                onChange={this.onSuggestionSelected.bind(this)}
              />
            )
          }
        }}
      </FieldLayoutBox>
    )
  }
}
class AutoSuggestBoxClassPlain3 extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      suggestionList: [],
      selectedSuggestion: null // Initialize as null
    }
  }

  static propTypes = buildPropTypes([], {
    queryName: pt.string.isRequired,
    restoreValue: pt.any,
    hideClear: pt.bool.isRequired,
    extraDataMap: pt.oneOfType([
      pt.string,
      pt.arrayOf(
        pt.shape({
          Source: pt.string,
          Dest: pt.string
        })
      ),
      pt.func
    ]),
    overrideSelectedSuggestion: pt.func,
    readonly: pt.bool
  });

  static defaultProps = {
    hideClear: false,
    readonly: false
  };

  onSuggestionsClearRequested () {
    this.setState({
      suggestionList: []
    })
  }

  componentDidMount () {
    const { restoreValue } = this.props

    if (restoreValue) {
      this.restoreSuggestion(restoreValue)
    }
  }

  componentDidUpdate (prevProps) {
    const restoreValue = componentGetCompareProps('restoreValue', this.props, prevProps, undefined)

    if (restoreValue.HasChanged) {
      console.log('Restoring suggestion for:', restoreValue.Current)
      this.restoreSuggestion(restoreValue.Current)
    }

    // Check if the data has finished loading and update state
    if (prevProps.data.loadingGetSuggestions && !this.props.data.loadingGetSuggestions) {
      if (!this.state.suggestionList.length) {
        this.setState({
          suggestionList: this.props.data.suggestions || []
        })
      }
    }
  }

  fetchSuggestions = _.debounce((value) => {
    const { queryName, GetSuggestions, formCheckForSubmissionErrors, overrideSuggestionList } = this.props

    GetSuggestions({
      Name: queryName,
      Type: queryName,
      Text: value,
      RestoreValue: value
    })
      .then(({ data }) => {
        const newData = _.isFunction(overrideSuggestionList)
          ? overrideSuggestionList(_.clone(data))
          : data

        this.setState({
          suggestionList: newData || []
        })
      })
      .catch(formCheckForSubmissionErrors)
  }, 300);

  onSuggestionsFetchRequested (event, value, reason) {
    if (reason === 'input') {
      this.fetchSuggestions(value)
    }
  }

  onSuggestionSelected (event, { suggestion }) {
    if (!suggestion || suggestion.Value === 'No Matches' || suggestion.Value === 'Searching...') {
      event.stopPropagation()
      event.nativeEvent.stopImmediatePropagation()
      return
    }

    const {
      input: { onChange },
      formSetFieldValue,
      meta: { form },
      extraDataMap,
      id,
      overrideSelectedSuggestion
    } = this.props

    this.setState(
      {
        selectedSuggestion: suggestion
      },
      () => {
        onChange(suggestion.Value)

        if (_.isFunction(overrideSelectedSuggestion)) {
          overrideSelectedSuggestion(suggestion, this.clearSelectedSuggestion)
        }

        if (extraDataMap) {
          const extraData = suggestion.ExtraData || {}
          if (_.isString(extraDataMap)) {
            formSetFieldValue(form, extraDataMap, suggestion.Text)
          } else if (_.isArray(extraDataMap)) {
            extraDataMap.forEach(mapInfo => {
              if (_.has(extraData, mapInfo.Source)) {
                formSetFieldValue(form, mapInfo.Dest, extraData[mapInfo.Source])
              }
            })
          } else if (_.isFunction(extraDataMap)) {
            extraDataMap(suggestion, extraData, (field, value) => {
              formSetFieldValue(form, field, value, 'set')
            })
          } else {
            throw Error('Invalid extraDataMap, must be a string, array, or function!')
          }
        }
      }
    )
  }

  clearSelectedSuggestion = () => {
    const {
      input: { onChange },
      formSetFieldValue,
      meta: { form },
      extraDataMap
    } = this.props

    this.setState(
      {
        selectedSuggestion: null // Clear the selected suggestion
      },
      () => {
        onChange(null)
        if (extraDataMap) {
          if (_.isString(extraDataMap)) {
            formSetFieldValue(form, extraDataMap, null)
          } else if (_.isArray(extraDataMap)) {
            extraDataMap.forEach(mapInfo => {
              formSetFieldValue(form, mapInfo.Dest, mapInfo.ClearValue)
            })
          } else if (_.isFunction(extraDataMap)) {
            extraDataMap({}, {}, (field, value) => {
              formSetFieldValue(form, field, value, 'clear')
            })
          } else {
            throw Error('Invalid extraDataMap, must be a string, array, or function!')
          }
        }
      }
    )
  }

  restoreSuggestion (value) {
    const { queryName, GetSuggestions, formCheckForSubmissionErrors } = this.props

    GetSuggestions({
      Name: queryName,
      Type: queryName,
      Text: null,
      Value: value
    })
      .then(({ data }) => {
        this.setState({
          selectedSuggestion: data[0] || null
        })
      })
      .catch(formCheckForSubmissionErrors)
  }

  render () {
    const { data, hideClear, readonly: lreadonly, id, ...rest } = this.props
    const { suggestionList, selectedSuggestion } = this.state

    const suggestions = data.loadingGetSuggestions ? [{ Value: 'Searching...' }] : (suggestionList.length > 0 ? suggestionList : [{ Value: 'No Matches' }])

    return (
      <FieldLayoutBox {...rest}>
        {({ showError, value, onChange, name, placeHolder = 'Start typing to search', loading, readonly }) =>
          <Autocomplete
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue)
              if (input && input.onChange) {
                input.onChange(newValue && newValue.Value ? newValue.Value : '')
              }
            }}
            inputValue={inputValue}
            onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
            options={options}
            getOptionLabel={(option) => option.Text || option.Value || ''}
            renderInput={(params) => (
              <TextField {...params} label={placeholder} fullWidth />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Grid container alignItems="center">
                  <Grid item sx={{ display: 'flex', width: 44 }}>
                    <LocationOnIcon sx={{ color: 'text.secondary' }} />
                  </Grid>
                  <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>
                      {option.Text}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {(option.ExtraData && option.ExtraData.SecondaryText) || ''}
                    </Typography>
                  </Grid>
                </Grid>
              </li>
            )}
            filterOptions={(x) => x}
            disabled={readonly}
            noOptionsText="No matches found"
          />
        }
      </FieldLayoutBox>
    )
  }
}

const AutoSuggestBoxClassPlain = ({ input, meta, sx, GetSuggestions, readonly, data, queryName, formCheckForSubmissionErrors, overrideSuggestionList, overrideSelectedSuggestion, placeholder = 'Start typing to search...', ...rest }) => {
  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const fetchSuggestions = useRef()

  // Debounced function for fetching suggestions
  fetchSuggestions.current = useMemo(
    () =>
      debounce((query, callback) => {
        GetSuggestions({
          Name: queryName,
          Type: queryName,
          Text: query,
          RestoreValue: query
        })
          .then(({ data }) => {
            const newData = _.isFunction(overrideSuggestionList)
              ? overrideSuggestionList(_.clone(data))
              : data

            setOptions(newData || [])
          }).catch(formCheckForSubmissionErrors)
      }, 300),
    [GetSuggestions]
  )

  useEffect(() => {
    let active = true

    if (inputValue === '') {
      setOptions(value ? [value] : [])
      return
    }

    fetchSuggestions.current(inputValue, (results) => {
      if (active) {
        const newOptions = value ? [value, ...results] : results
        setOptions(newOptions)
      }
    })

    return () => {
      active = false
    }
  }, [inputValue, value])

  return (
    <Autocomplete sx={sx}
      value={value}
      onChange={(event, newValue) => {
        setValue(newValue)
        if (input && input.onChange) {
          input.onChange(newValue && newValue.Value ? newValue.Value : '')
        }
        if (_.isFunction(overrideSelectedSuggestion) && newValue) {
          overrideSelectedSuggestion(newValue)
        }
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === 'input' || reason === 'reset') setInputValue(newInputValue)
        if (reason === 'clear') setInputValue('')
      }}
      options={options}
      getOptionLabel={(option) => option.Text || option.Value || ''}
      renderInput={(params) => (
        <TextField {...params} label={placeholder} fullWidth />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Grid container alignItems="center">
            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
              <Box component="span" sx={{ fontWeight: 'bold' }}>
                {option.Text}
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {(option.ExtraData && option.ExtraData.DisplayName) || ''}
              </Typography>
            </Grid>
          </Grid>
        </li>
      )}
      filterOptions={(x) => x}
      disabled={readonly}
      noOptionsText="No matches found"
    />
  )
}

const autoSuggestBoxes = new Map()

const BuildAutoSuggestBox = (name) => {
  let ret = autoSuggestBoxes.get(name)

  if (!ret) {
    ret = compose(
      ReduxWrapper([], [ah.formCheckForSubmissionErrors, ah.formSetFieldValue]),
      QueryWrapper({
        key: 'AutoSuggest',
        actions: [
          {
            url: '/AutoComplete/Get',
            prop: 'GetSuggestions',
            map: false
          }
        ]
      })
    )(AutoSuggestBoxClassPlain)
    autoSuggestBoxes.set(name, ret)
  }

  return ret
}

export const AutoSuggestMU = ({ parse, format, fieldType, ...rest }) => {
  return <Field parse={fieldType ? FieldValueParser(fieldType) : parse}
    format={fieldType ? FieldValueFormatter(fieldType) : format}
    {...rest}
  component={BuildAutoSuggestBox(rest.name)} /> } // eslint-disable-line

export default AutoSuggestMU
